import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "../services/api";
import { connect, subscribe } from "../services/lockSocket";

const HEARTBEAT_MS = 30_000;

export function useRowEditing(pharmacyId, currentUserEmail, options = {}) {
    // commandePatientId sélectionné par l'utilisateur courant
    const [activeId, setActiveId] = useState(null);
    // Map<commandePatientId, email> pour les éditions des autres users
    const [editingMap, setEditingMap] = useState({});

    const activeIdRef = useRef(null);
    activeIdRef.current = activeId;
    const heartbeatRef = useRef(null);
    const beforeUnlockRef = useRef(options.onBeforeUnlock);
    beforeUnlockRef.current = options.onBeforeUnlock;

    const stopHeartbeat = useCallback(() => {
        if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
    }, []);

    const releaseLock = useCallback((commandePatientId) => (
        Promise.resolve(API.delete(`/commande-patients/${commandePatientId}/row-lock`, { noRedirectOn401: true })).catch(() => {})
    ), []);

    const select = useCallback(async (commandePatientId) => {
        if (!commandePatientId) return false;
        if (activeIdRef.current === commandePatientId) {
            // Déjà sélectionné → déselectionner
            const saved = await beforeUnlockRef.current?.(commandePatientId);
            if (saved === false) return true;
            await releaseLock(commandePatientId);
            stopHeartbeat();
            setActiveId(null);
            return false;
        }

        const previousId = activeIdRef.current;
        if (previousId) {
            const saved = await beforeUnlockRef.current?.(previousId);
            if (saved === false) return false;
        }

        try {
            await API.post(`/commande-patients/${commandePatientId}/row-lock`, {}, { noRedirectOn401: true });
            setActiveId(commandePatientId);
            stopHeartbeat();
            heartbeatRef.current = setInterval(() => {
                API.put(`/commande-patients/${commandePatientId}/row-lock`, {}, { noRedirectOn401: true }).catch(() => {});
            }, HEARTBEAT_MS);
            return true;
        } catch {
            // Lock refusé : déjà tenu par un autre
            return false;
        }
    }, [releaseLock, stopHeartbeat]);

    const releaseActive = useCallback(async () => {
        const id = activeIdRef.current;
        if (!id) return;
        const saved = await beforeUnlockRef.current?.(id);
        if (saved === false) return;
        await releaseLock(id);
        stopHeartbeat();
        setActiveId(null);
    }, [releaseLock, stopHeartbeat]);

    // Connexion WS + écoute événements row
    useEffect(() => {
        if (!pharmacyId) return;
        connect(pharmacyId);
        const unsubscribe = subscribe((event) => {
            const { type, commandePatientId: cpId, editedBy } = event;
            const id = Number(cpId);
            if (type === "ROW_EDITING_START") {
                if (editedBy && editedBy !== currentUserEmail) {
                    setEditingMap(prev => ({ ...prev, [id]: editedBy }));
                }
            } else if (type === "ROW_EDITING_STOP") {
                setEditingMap(prev => ({ ...prev, [id]: null }));
            }
        });
        return () => {
            if (typeof unsubscribe === "function") unsubscribe();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pharmacyId, currentUserEmail]);

    // Libérer au démontage
    useEffect(() => {
        return () => {
            stopHeartbeat();
            if (activeIdRef.current) {
                const id = activeIdRef.current;
                Promise.resolve(beforeUnlockRef.current?.(id)).finally(() => releaseLock(id));
            }
        };
    }, [releaseLock, stopHeartbeat]);

    return { activeId, editingMap, select, releaseActive };
}
